import { Controller, useFormContext } from 'react-hook-form';
import {
	FormErrorMessage,
	FormLabel,
	FormMessage,
} from '@gov-design-system-ce/react-form';
import { useGovFormContext } from '@gov-design-system-ce/react-form-core';
import {
	GovFormControl,
	GovFormGroup,
} from '@gov-design-system-ce/react/gov-form';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { WebFileProps } from '@gov-nx/ui/types/forms/FormFile';
import { formatBytes } from '@gov-nx/utils/common/File/file-size';
import { Button } from '../../Button/Button';
import { FormFile } from '../../FormFile';
import { Icon } from '../../Icon/Icon';
import { LayoutGap } from '../../Layout/LayoutGap';
import { FormUploadAttachments } from './FormUploadAttachments';

export const FormFileUpload = ({
	field,
	label,
	message,
	group,
	fileUploadHook,
	control,
}: WebFileProps) => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	const formContext = useFormContext();
	const extraProps = useGovFormContext().propsFromSchema(field.name);

	const { files, filesWeb, addFiles, removeFile, options } = fileUploadHook;

	const canAdd = files.length < options.maxAttachments;
	const isDisabled = !canAdd || field.disabled;

	return (
		<Controller
			name={field.name}
			control={formContext.control}
			render={({ field: renderField, fieldState }) => (
				<div>
					<GovFormControl {...control}>
						{label ? (
							<FormLabel
								slot="top"
								isRequired={extraProps.isRequired}
								{...label}>
								{label.children}
							</FormLabel>
						) : null}
						<GovFormGroup {...group}>
							<FormFile
								ref={renderField.ref}
								isInvalid={fieldState.invalid}
								name={field.name}
								isRequired={extraProps.isRequired}
								isExpanded={field.expanded}
								isDisabled={isDisabled}
								accept={options.extensions.join(',')}
								maxFileSize={options.maxFileSize}
								isMultiple={options.multiple}
								files={filesWeb}
								onFilesUpdated={(files) => addFiles(files ?? [])}
								resetAfterPick={true}
								displayAttachments={false}>
								<LayoutGap gap="s">
									<p className="text-body-m">
										{t('nahrani-souboru.pretahnete-soubor-nebo')}
									</p>
									<p aria-hidden="true">
										<Button
											isDisabled={isDisabled}
											isFocusable={false}
											aria-label={t(
												'nahrani-souboru.nahrat-soubor-ze-zarizeni'
											)}
											color="primary"
											type="outlined"
											iconStart={
												<Icon
													type="components"
													name="upload"
												/>
											}>
											{t('nahrani-souboru.nahrat-soubor-ze-zarizeni')}
										</Button>
									</p>
									{options.extensions.length ? (
										<p className="gov-text--s">
											{t('nahrani-souboru.pocet-velikost-souboru', {
												count: options.maxAttachments,
												sumSize: formatBytes(options.maxSumFileSize),
											})}
											<br />
											{t('nahrani-souboru.podporovane-formaty', {
												extensions: options.extensions
													.map((ext) => ext.replace('.', '').toUpperCase())
													.join(', '),
											})}
										</p>
									) : null}
								</LayoutGap>
							</FormFile>
						</GovFormGroup>
						<div className="mt-0!">
							{fieldState.error?.message && (
								<FormErrorMessage message={fieldState.error.message} />
							)}
							{message && <FormMessage messages={message} />}
						</div>
					</GovFormControl>

					<FormUploadAttachments
						onRemove={removeFile}
						files={files}
					/>
				</div>
			)}
		/>
	);
};
